import { useState } from "react";
import Successfully from "../../../assets/images/myaccount/successfully-img.png";
import { useMutation } from "@tanstack/react-query";
import { changePasswordAPI } from "../../../api/auth";
import useToast from "../../../hooks/useToast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../../button/PrimaryBtn";

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });
  if (!isOpen) return null;

  const { mutate, isPending } = useMutation({
    mutationFn: changePasswordAPI,
    onSuccess: (data) => {
      setIsSuccess(true);
      // useToast(t("changePasswordModal.passwordChangedSuccessfully"), "success");
    },
    onError: (error: Error) => {
      useToast(error.message || t("changePasswordModal.failedToChangePassword"), "error");
      setIsSuccess(false);
    },
  });

  const initialValues = {
    old_password: "",
    new_password: "",
    confirm_password: "",
  };

  const validationSchema = Yup.object().shape({
    old_password: Yup.string().required(t("changePasswordModal.oldPasswordRequired")),
    new_password: Yup.string()
      .required(t("changePasswordModal.pleaseEnterYourPassword"))
      .min(8, t("changePasswordModal.passwordMustBeAtLeast8Characters"))
      .matches(/[A-Z]/, t("changePasswordModal.passwordMustContainAtLeastOneUppercaseLetter"))
      .matches(/[a-z]/, t("changePasswordModal.passwordMustContainAtLeastOneLowercaseLetter"))
      .matches(/[0-9]/, t("changePasswordModal.passwordMustContainAtLeastOneNumber"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t("changePasswordModal.passwordMustContainAtLeastOneSpecialCharacter")),
    confirm_password: Yup.string()
      .required(t("changePasswordModal.confirmPasswordRequired"))
      .oneOf([Yup.ref("new_password")], t("changePasswordModal.passwordsMustMatch")),
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, resetForm } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Only send old_password and new_password to API
      mutate({
        old_password: values.old_password,
        new_password: values.new_password,
      });
    },
  });

  return (
    <>
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
        <div className="modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 lg:max-w-[35vw] md:max-w-[543] max-w-116 w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll">
          <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] md:mb-0 mb-3">
            {/* Close Button */}
            <button
              onClick={() => {
                setIsSuccess(false);
                resetForm();
                onClose();
              }}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto lg:mb-6 md:mb-4 mb-2 hover:scale-110 transition-transform duration-300"
            >
              <svg
                className="w-full h-full"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                  fill="#333938"
                />
              </svg>
            </button>
          </div>
          {!isSuccess && (
            <div>
              {/* Header */}
              <div className="text-center lg:mb-[1.2vw] mb-4">
                <h1 className="lg:text-[1.5vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
                  {t("changePasswordModal.changePassword")}
                </h1>
                <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
                  {t("changePasswordModal.enterANewPasswordToResetAndSecureYourAccount")}
                </p>
              </div>

              <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
                <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <input
                    type={showPassword.old_password ? "text" : "password"}
                    name="old_password"
                    placeholder={t("changePasswordModal.enterYourOldPassword")}
                    value={values.old_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword({ ...showPassword, old_password: !showPassword.old_password })}
                    className="absolute end-4 top-1/2 -translate-y-1/2 text-black-400 hover:text-black-300 transition-colors"
                  >
                    {showPassword.old_password ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.old_password && touched.old_password && (
                  <p className="text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                    {errors.old_password}
                  </p>
                )}
              </div>

              <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
                <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <input
                    type={showPassword.new_password ? "text" : "password"}
                    placeholder={t("changePasswordModal.enterYourNewPassword")}
                    name="new_password"
                    value={values.new_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword({ ...showPassword, new_password: !showPassword.new_password })
                    }
                    className="absolute end-4 top-1/2 -translate-y-1/2 text-black-400 hover:text-black-300 transition-colors"
                  >
                    {showPassword.new_password ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.new_password && touched.new_password && (
                  <p className="text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                    {errors.new_password}
                  </p>
                )}
              </div>

              <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
                <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                  <input
                    type={showPassword.confirm_password ? "text" : "password"}
                    placeholder={t("changePasswordModal.reEnterYourNewPassword")}
                    name="confirm_password"
                    value={values.confirm_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword({ ...showPassword, confirm_password: !showPassword.confirm_password })
                    }
                    className="absolute end-4 top-1/2 -translate-y-1/2 text-black-400 hover:text-black-300 transition-colors"
                  >
                    {showPassword.confirm_password ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirm_password && touched.confirm_password && (
                  <p className="text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
                    {errors.confirm_password}
                  </p>
                )}
              </div>

              {/* Send Reset Link Button */}
              <PrimaryBtn
                type={isPending ? "button" : "submit"}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                disabled={isPending}
                className="min-w-full"
              >
                {isPending ? t("changePasswordModal.changing") : t("changePasswordModal.change")}
              </PrimaryBtn>
            </div>
          )}
          {isSuccess && (
            <div className="text-center">
              <img
                src={Successfully}
                alt=""
                className="mx-auto 2xl:max-w-[6.78vw] max-w-[130px] w-full 2xl:mb-[6.78vh] md:mb-[30px] mb-5 rtl:-scale-x-100"
              />
              <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
                {t("changePasswordModal.changePasswordSuccessfully")}
              </h1>
              <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold lg:mb-[1.1vw] md:mb-5 mb-4">
                {t("changePasswordModal.yourNewPasswordIsNowActiveYouCanContinueUsingYourAccountSecurely")}
              </p>
              <PrimaryBtn
                onClick={() => {
                  setIsSuccess(false);
                  resetForm();
                  onClose();
                }}
                className="min-w-full"
              >
                {t("changePasswordModal.okay")}
              </PrimaryBtn>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default ChangePasswordModal;
